// src/pages/admin-audit-management/components/AuditUploadInterface.jsx
import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { auditReportService } from '../../../services/auditReportService';
import { useAuth } from '../../../contexts/AuthContext';

const AuditUploadInterface = ({ onFileUpload, submissionId = null, userId = null }) => {
  const { user } = useAuth();
  const [isDragging, setIsDragging] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isValidating, setIsValidating] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const fileInputRef = useRef(null);

  const handleDragEnter = (e) => { e?.preventDefault(); e?.stopPropagation(); setIsDragging(true); };
  const handleDragLeave = (e) => { e?.preventDefault(); e?.stopPropagation(); setIsDragging(false); };
  const handleDragOver  = (e) => { e?.preventDefault(); e?.stopPropagation(); };

  const handleDrop = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setIsDragging(false);
    processFiles(Array.from(e?.dataTransfer?.files));
  };

  const handleFileSelect = (e) => {
    processFiles(Array.from(e?.target?.files));
    e.target.value = '';
  };

  // ─── JSON structure validation ────────────────────────────────────
  const validateJSONStructure = (json) => {
    const errors = [];
    const required = ['report_type', 'meta', 'cover', 'score_overview', 'slide_analysis'];
    required.forEach(f => { if (!json[f]) errors.push(`Missing required field: ${f}`); });
    if (json.cover && !json.cover.COMPANY_NAME) errors.push('cover.COMPANY_NAME is required');
    if (json.cover && !json.cover.SCORE)        errors.push('cover.SCORE is required');
    if (json.slide_analysis && !json.slide_analysis.slides) errors.push('slide_analysis.slides array is required');
    return errors;
  };

  // ─── Generate unique report ID ────────────────────────────────────
  const generateUniqueReportId = () => {
    const timestamp = Date.now();
    const randomStr = Math.random().toString(36).substr(2, 9).toUpperCase();
    return `RPT-${timestamp}-${randomStr}`;
  };

  // ─── Extract preview metadata from JSON ──────────────────────────
  const extractPreview = (jsonContent) => ({
    company:     jsonContent.cover?.COMPANY_NAME,
    deckName:    jsonContent.cover?.DECK_NAME,
    score:       jsonContent.cover?.SCORE,
    // ✅ TOTAL_SLIDES is in slide_analysis, not cover
    totalSlides: jsonContent.slide_analysis?.TOTAL_SLIDES
              || jsonContent.cover?.TOTAL_SLIDES
              || jsonContent.slide_analysis?.slides?.length
              || 0,
    // ✅ key is critical_issues (not all_issues) in FREE reports
    issuesCount: (
      jsonContent.critical_issues ||
      jsonContent.all_issues ||
      []
    ).length,
  });

  // ─── Main file processor ──────────────────────────────────────────
  const processFiles = async (files) => {
    setIsValidating(true);
    setError(null);
    setSuccess(null);

    const validFiles = files.filter(f => {
      const isJson = f.type === 'application/json' || f.name.endsWith('.json');
      if (!isJson) return false;
      if (f.size > 50 * 1024 * 1024) {
        setError(`"${f.name}" exceeds 50 MB. Please check the file.`);
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) {
      setError('No valid JSON files found. Please upload .json audit files.');
      setIsValidating(false);
      return;
    }

    const processedFiles = validFiles.map(file => ({
      id: `file-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name: file.name,
      size: file.size,
      uploadDate: new Date().toISOString(),
      status: 'validating',
      file,
      type: 'json',
      validationErrors: [],
      preview: null,
      jsonContent: null,
    }));

    setUploadedFiles(prev => [...prev, ...processedFiles]);

    const validatedFiles = [];
    for (const fileObj of processedFiles) {
      try {
        const reader = new FileReader();
        const jsonContent = await new Promise((resolve, reject) => {
          reader.onload = (e) => {
            try { resolve(JSON.parse(e.target.result)); }
            catch { reject(new Error('Invalid JSON — file could not be parsed.')); }
          };
          reader.onerror = () => reject(new Error('Could not read file.'));
          reader.readAsText(fileObj.file);
        });

        const validationErrors = validateJSONStructure(jsonContent);

        if (validationErrors.length > 0) {
          fileObj.status = 'error';
          fileObj.validationErrors = validationErrors;
        } else {
          fileObj.status = 'validated';
          fileObj.jsonContent = jsonContent;
          fileObj.preview = extractPreview(jsonContent);
        }
        validatedFiles.push(fileObj);
      } catch (err) {
        fileObj.status = 'error';
        fileObj.validationErrors = [err.message];
        validatedFiles.push(fileObj);
      }
    }

    setUploadedFiles(prev =>
      prev.map(f => validatedFiles.find(vf => vf.id === f.id) || f)
    );
    setIsValidating(false);

    for (const fileObj of validatedFiles.filter(f => f.status === 'validated')) {
      await uploadJSONFile(fileObj);
    }

    onFileUpload(validatedFiles.filter(f => f.status === 'validated'));
  };

  // ─── Upload + HTML generation ─────────────────────────────────────
  const uploadJSONFile = async (fileObj) => {
    try {
      console.log('👤 Auth check - user from context:', user);
      const activeUserId = userId || user?.id;
      if (!activeUserId) throw new Error('You must be logged in to upload files.');

      const jsonContent = fileObj.jsonContent;
      if (!jsonContent) throw new Error('JSON content missing — please re-validate the file.');

      const uniqueReportId = generateUniqueReportId();
      console.log('📋 Generated unique report ID:', uniqueReportId);

      setUploadProgress(prev => ({ ...prev, [fileObj.id]: 20 }));

      const result = await auditReportService.uploadJSONAuditReport(
        jsonContent,
        fileObj.file,
        submissionId,
        activeUserId,
        uniqueReportId
      );

      setUploadProgress(prev => ({ ...prev, [fileObj.id]: 50 }));

      console.log('🎯 Auto-generating HTML from JSON...');
      let templateServiceModule;
      try {
        templateServiceModule = await import('../../../services/templateService.js');
      } catch {
        templateServiceModule = await import('../../../services/templateService');
      }
      const templateService = templateServiceModule.templateService || templateServiceModule.default;
      if (!templateService || typeof templateService.generateAndSave !== 'function') {
        throw new Error('templateService.generateAndSave not found — check the service file.');
      }

      const companyName =
        jsonContent.cover?.COMPANY_NAME ||
        jsonContent.company_name ||
        fileObj.name.replace('.json', '');

      const htmlResult = await templateService.generateAndSave(
        result.id,
        jsonContent,
        activeUserId,
        submissionId,
        uniqueReportId
      );
      setUploadProgress(prev => ({ ...prev, [fileObj.id]: 80 }));

      await auditReportService.updateAuditStatus(result.id, 'completed', {
        html_url: htmlResult.bucketInfo.publicUrl,
        html_storage_path: htmlResult.bucketInfo.filePath,
        html_generated_at: new Date().toISOString(),
        report_id: uniqueReportId,
      });
      setUploadProgress(prev => ({ ...prev, [fileObj.id]: 100 }));

      setUploadedFiles(prev =>
        prev.map(f =>
          f.id === fileObj.id
            ? {
                ...f,
                status: 'uploaded',
                uploadResult: {
                  ...result,
                  htmlUrl: htmlResult.bucketInfo.publicUrl,
                  htmlGenerated: true,
                  reportId: uniqueReportId,
                },
                preview: {
                  ...f.preview,
                  reportId: uniqueReportId,
                },
              }
            : f
        )
      );

      setSuccess(`✅ HTML generated successfully for: ${fileObj.preview?.company || fileObj.name} (Report ID: ${uniqueReportId})`);
      setTimeout(() => setUploadProgress(prev => { const p = { ...prev }; delete p[fileObj.id]; return p; }), 3000);
      setTimeout(() => setSuccess(null), 5000);

    } catch (err) {
      console.error('❌ Upload error:', err);
      setUploadedFiles(prev =>
        prev.map(f =>
          f.id === fileObj.id ? { ...f, status: 'error', validationErrors: [err.message] } : f
        )
      );
      if (err.message.includes('session') || err.message.includes('expired')) {
        setError('Authentication error. Please log out and log in again.');
      } else {
        setError(`Failed to process "${fileObj.name}": ${err.message}`);
      }
      setTimeout(() => setError(null), 8000);
    }
  };

  const removeFile = (fileId) => {
    setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
    setUploadProgress(prev => { const p = { ...prev }; delete p[fileId]; return p; });
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getStatusIcon = (file) => {
    switch (file.status) {
      case 'validating': return <Icon name="Loader2" size={18} className="animate-spin text-primary" />;
      case 'validated':  return <Icon name="CheckCircle2" size={18} color="var(--color-success)" />;
      case 'uploaded':   return <Icon name="Cloud" size={18} color="var(--color-primary)" />;
      case 'error':      return <Icon name="AlertCircle" size={18} color="var(--color-destructive)" />;
      default:           return null;
    }
  };

  return (
    <div className="space-y-4 md:space-y-6">

      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
          <Icon name="FileJson2" size={22} color="var(--color-primary)" />
        </div>
        <div>
          <h3 className="text-base font-semibold text-foreground">Upload JSON Audit File</h3>
          <p className="text-xs text-muted-foreground">JSON is parsed and HTML report is auto-generated</p>
        </div>
      </div>

      {/* Error / Success */}
      {error && (
        <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start gap-2">
          <Icon name="AlertCircle" size={16} color="var(--color-destructive)" className="mt-0.5 flex-shrink-0" />
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}
      {success && (
        <div className="p-4 bg-success/10 border border-success/20 rounded-lg flex items-start gap-2">
          <Icon name="CheckCircle2" size={16} color="var(--color-success)" className="mt-0.5 flex-shrink-0" />
          <p className="text-sm text-success">{success}</p>
        </div>
      )}

      {/* Drop Zone */}
      <div
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`relative border-2 border-dashed rounded-xl p-8 md:p-12 transition-all cursor-pointer ${
          isDragging ? 'border-primary bg-primary/5' : 'border-border bg-muted/30 hover:border-primary/50'
        }`}
        onClick={handleBrowseClick}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".json,application/json"
          multiple
          onChange={handleFileSelect}
          className="hidden"
        />
        <div className="flex flex-col items-center justify-center text-center space-y-4">
          <div className={`w-16 h-16 rounded-full flex items-center justify-center transition-all ${
            isDragging ? 'bg-primary/20' : 'bg-primary/10'
          }`}>
            <Icon name={isDragging ? 'Download' : 'FileJson2'} size={30} color="var(--color-primary)" />
          </div>
          <div className="space-y-1">
            <h3 className="text-lg font-semibold text-foreground">
              {isDragging ? 'Drop JSON files here' : 'Drag & drop JSON audit files'}
            </h3>
            <p className="text-sm text-muted-foreground">or click anywhere to browse</p>
            <p className="text-xs text-primary mt-1">✨ HTML report is auto-generated from each JSON file</p>
          </div>
          <div className="flex items-center gap-4 text-xs text-muted-foreground pointer-events-none">
            <span className="flex items-center gap-1"><Icon name="FileJson2" size={12} /> .json files only</span>
            <span className="flex items-center gap-1"><Icon name="Shield" size={12} /> Auto-validated</span>
            <span className="flex items-center gap-1"><Icon name="Zap" size={12} /> Auto HTML generation</span>
          </div>
        </div>
      </div>

      {/* File List */}
      {uploadedFiles.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-foreground">Files ({uploadedFiles.length})</h4>
            {isValidating && (
              <span className="text-xs text-muted-foreground flex items-center gap-1.5">
                <Icon name="Loader2" size={12} className="animate-spin" /> Validating…
              </span>
            )}
          </div>

          <div className="space-y-2">
            {uploadedFiles.map(file => (
              <div key={file.id} className="flex flex-col p-4 bg-card border border-border rounded-lg hover:border-primary/30 transition-all">

                {/* Row 1 — name + status */}
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Icon name="FileJson2" size={20} color="var(--color-primary)" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{file.name}</p>
                      <p className="text-xs text-muted-foreground">{formatFileSize(file.size)} · JSON</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {file.uploadResult?.htmlUrl && (
                      <a
                        href={file.uploadResult.htmlUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="View generated HTML"
                        className="p-1.5 rounded hover:bg-primary/10 text-primary"
                        onClick={e => e.stopPropagation()}
                      >
                        <Icon name="ExternalLink" size={16} />
                      </a>
                    )}
                    {getStatusIcon(file)}
                    <button
                      onClick={() => removeFile(file.id)}
                      className="p-1.5 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-all"
                    >
                      <Icon name="X" size={16} />
                    </button>
                  </div>
                </div>

                {/* Row 2 — JSON preview metadata */}
                {file.preview && (
                  <div className="mt-3 pt-3 border-t border-border grid grid-cols-2 md:grid-cols-5 gap-3">
                    <div>
                      <p className="text-xs text-muted-foreground">Company</p>
                      <p className="text-sm font-medium truncate">{file.preview.company || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Score</p>
                      <p className="text-sm font-medium">{file.preview.score ?? '—'}/100</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Slides</p>
                      <p className="text-sm font-medium">{file.preview.totalSlides || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Issues</p>
                      <p className="text-sm font-medium">{file.preview.issuesCount ?? 0}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Report ID</p>
                      <p className="text-sm font-medium truncate text-primary">{file.preview.reportId || '—'}</p>
                    </div>
                  </div>
                )}

                {/* Row 3 — generated HTML link */}
                {file.uploadResult?.htmlUrl && (
                  <div className="mt-3 pt-3 border-t border-border flex items-center justify-between">
                    <p className="text-xs text-muted-foreground">Generated HTML report:</p>
                    <a
                      href={file.uploadResult.htmlUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-primary hover:underline flex items-center gap-1"
                    >
                      View Report <Icon name="ExternalLink" size={11} />
                    </a>
                  </div>
                )}

                {/* Row 4 — validation errors */}
                {file.validationErrors?.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-destructive/20">
                    <p className="text-xs font-medium text-destructive mb-1">Validation errors:</p>
                    <ul className="space-y-0.5">
                      {file.validationErrors.map((err, i) => (
                        <li key={i} className="text-xs text-destructive/80 flex items-start gap-1">
                          <span className="mt-0.5">•</span> {err}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Row 5 — upload progress */}
                {uploadProgress[file.id] !== undefined && (
                  <div className="mt-3">
                    <div className="flex justify-between mb-1">
                      <span className="text-xs text-muted-foreground">
                        {uploadProgress[file.id] < 50 ? 'Saving JSON…' :
                         uploadProgress[file.id] < 80 ? 'Generating HTML…' : 'Finalizing…'}
                      </span>
                      <span className="text-xs font-medium">{uploadProgress[file.id]}%</span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-1.5">
                      <div
                        className="bg-primary h-1.5 rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress[file.id]}%` }}
                      />
                    </div>
                  </div>
                )}

              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  function handleBrowseClick() { fileInputRef?.current?.click(); }
};

export default AuditUploadInterface;